import { createI18n } from 'vue-i18n';
import sharedDe from '@/shared/i18n/de';
import sharedEn from '@/shared/i18n/en';
import employeeDe from '@/areas/employee/i18n/de';
import employeeEn from '@/areas/employee/i18n/en';
import customerDe from '@/areas/customer/i18n/de';
import customerEn from '@/areas/customer/i18n/en';

/**
 * The languages the app is translated into. Mirrors the backend
 * `SupportedLanguage` enum (source of truth) — keep the two in sync.
 */
export const SUPPORTED_LANGUAGES = ['de', 'en'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

/** Used when neither the user nor the browser yields a supported language. */
export const DEFAULT_LANGUAGE: Language = 'de';

export function isSupported(value: string | null | undefined): value is Language {
  return value != null && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Maps an arbitrary BCP-47 tag (e.g. `de-AT`, `EN`, `en-US`) to a supported
 * language by its primary subtag, or `null` if none matches.
 */
export function mapToSupported(raw: string | null | undefined): Language | null {
  if (!raw) return null;
  const primary = raw.trim().toLowerCase().split('-')[0];
  return isSupported(primary) ? primary : null;
}

/**
 * Resolves the effective UI language. Order:
 *   1. the user's explicit choice (`null` until they pick one)
 *   2. the browser's preferred language, mapped to a supported one
 *   3. {@link DEFAULT_LANGUAGE}
 *
 * A browser-derived language is never persisted — it only drives the UI for
 * this session.
 */
export function resolveLocale(userLanguage: string | null | undefined): Language {
  return mapToSupported(userLanguage) ?? mapToSupported(navigator.language) ?? DEFAULT_LANGUAGE;
}

// Catalogs are namespaced per area: `shared.*`, `employee.*`, `customer.*`.
// Each area owns a per-language file (`i18n/de.ts`, `i18n/en.ts`); they are
// composed into one message tree per locale here.
const messages = {
  de: {shared: sharedDe, employee: employeeDe, customer: customerDe},
  en: {shared: sharedEn, employee: employeeEn, customer: customerEn},
};

export const i18n = createI18n({
  legacy: false,
  locale: DEFAULT_LANGUAGE,
  fallbackLocale: DEFAULT_LANGUAGE,
  messages,
});

/** Switches the active UI language live. */
export function setLocale(language: Language): void {
  i18n.global.locale.value = language;
}

/**
 * Full BCP-47 locale tags per supported language, used for `Intl`-based
 * formatting (dates, numbers). English maps to `en-GB` because the app runs in
 * a European (day-first) context — change here if that ever needs to differ.
 */
const INTL_LOCALES: Record<Language, string> = {
  de: 'de-DE',
  en: 'en-GB',
};

/**
 * The Intl locale tag for the currently active UI language. Reading it inside a
 * component render (e.g. via {@link formatDateTime}) registers the reactive
 * i18n locale as a dependency, so formatted output re-renders on a language
 * switch.
 */
export function currentIntlLocale(): string {
  const active = i18n.global.locale.value as Language;
  return INTL_LOCALES[active] ?? INTL_LOCALES[DEFAULT_LANGUAGE];
}
